import { gql } from "@apollo/client";
import {
  GET_EMPLOYEE,
  GET_EMPLOYEE_ONLY,
  GET_EMPLOYEE_ROW,
  GET_VISA,
} from "../fragments/processes";

export const GET_ALL_EMPLOYEES = gql`
  ${GET_EMPLOYEE_ROW}
  query GetEmployees(
    $searchText: String
    $status: EmployeeStatus
    $statuses: [EmployeeStatus!]
    $nationality: Nationalities
    $appliedCountry: CountriesList
    $region: String
    $brokerName: String
    $maritalStatus: MaritalStatus
    $religion: ReligionTypes
    $profession: ProfessionalTypes
    $arabic: LanguageExperience
    $amharic: LanguageExperience
    $english: LanguageExperience
    $ageStart: Int
    $ageEnd: Int
    $experienceYears: Int
    $skip: Int!
    $take: Int!
    $link: String
    $agencyId: String
    $agentId: String
    $cocCompleted: Boolean
    $medicalCompleted: Boolean
    $biometricsCompleted: Boolean
    $visaIssued: Boolean
    $labourIssued: Boolean
    $flightBooked: Boolean
    $contractedPrep: Boolean
    $embassyPrep: Boolean
    $lmisPrep: Boolean
  ) {
    employees(
      skip: $skip
      take: $take
      searchText: $searchText
      status: $status
      statuses: $statuses
      nationality: $nationality
      appliedCountry: $appliedCountry
      region: $region
      brokerName: $brokerName
      maritalStatus: $maritalStatus
      religion: $religion
      profession: $profession
      arabic: $arabic
      amharic: $amharic
      english: $english
      ageStart: $ageStart
      ageEnd: $ageEnd
      experienceYears: $experienceYears
      link: $link
      agencyId: $agencyId
      agentId: $agentId
      cocCompleted: $cocCompleted
      medicalCompleted: $medicalCompleted
      biometricsCompleted: $biometricsCompleted
      visaIssued: $visaIssued
      labourIssued: $labourIssued
      flightBooked: $flightBooked
      contractedPrep: $contractedPrep
      embassyPrep: $embassyPrep
      lmisPrep: $lmisPrep
    ) {
      totalCount
      employees {
        ...getEmployeeRow
      }
    }
  }
`;

/** Status histogram + process-tab totals (same filter scope as arguments). */
export const GET_EMPLOYEE_DASHBOARD_STATS = gql`
  query EmployeeDashboardStats(
    $searchText: String
    $status: EmployeeStatus
    $nationality: Nationalities
    $appliedCountry: CountriesList
    $region: String
    $brokerName: String
    $maritalStatus: MaritalStatus
    $religion: ReligionTypes
    $profession: ProfessionalTypes
    $arabic: LanguageExperience
    $amharic: LanguageExperience
    $english: LanguageExperience
    $ageStart: Int
    $ageEnd: Int
    $experienceYears: Int
    $agencyId: String
    $agentId: String
    $cocCompleted: Boolean
    $medicalCompleted: Boolean
    $biometricsCompleted: Boolean
  ) {
    employeeDashboardStats(
      searchText: $searchText
      status: $status
      nationality: $nationality
      appliedCountry: $appliedCountry
      region: $region
      brokerName: $brokerName
      maritalStatus: $maritalStatus
      religion: $religion
      profession: $profession
      arabic: $arabic
      amharic: $amharic
      english: $english
      ageStart: $ageStart
      ageEnd: $ageEnd
      experienceYears: $experienceYears
      agencyId: $agencyId
      agentId: $agentId
      cocCompleted: $cocCompleted
      medicalCompleted: $medicalCompleted
      biometricsCompleted: $biometricsCompleted
    ) {
      totalCount
      byStatus {
        status
        count
      }
      byAppliedCountry {
        country
        count
      }
      visaIssuedTrueCount
      labourIssuedTrueCount
      embassyProcessTabCount
      lmisProcessTabCount
      ticketingTabCount
      contractedPrepTabCount
    }
  }
`;

export const GET_SHARED_EMPLOYEES = gql`
  ${GET_EMPLOYEE_ONLY}
  query GetSharedEmployees($link: String) {
    sharedEmployees(link: $link) {
      totalCount
      employees {
        ...getEmployeeOnly
      }
      header {
        _id
        displayName
        header
        address {
          mobile
          country
        }
        cvTemplate {
          name
          numberOfPages
          cvLang
          bgColor
          headFontColor
          headBgColor
          labelFontColor
          labelBgColor
          valueFontColor
          valueBgColor
          borderColor
          borderRadius
          borderStyle
          borderWidth
          spacing
          labelFontSize
          valueFontSize
          labelFontWeight
          valueFontWeight
          headerHeight
          bodyHeight
          textTransform
          headerGradient
          evenGradient
          oddGradient
          addPassportHeader
          useHeaderGradient
          useBodyGradient
          headerShadow
          bodyShadow
          photoShadow
          passportShadow
          qrLocationLink
          qrLocationWidth
          qrLocationHeight
          qrLocationImageUrl
          qrLocationMargin
          qrMoreCvsLink
          qrMoreCvsWidth
          qrMoreCvsHeight
          qrMoreCvsImageUrl
          qrMoreCvsMargin
          faceHeight
          faceWidth
          standHeight
          standWidth
          flagHeight
          flagWidth
          passportHeight
          passportWidth
          headBgColorAlpha
          headGrFirstColorAlpha
          headGrSecondColorAlpha
          labelBgColorAlpha
          oddGrFirstColorAlpha
          oddGrSecondColorAlpha
          valueBgColorAlpha
          evenGrFirstColorAlpha
          evenGrSecondColorAlpha
        }
      }
    }
  }
`;
export const GET_LINKED_EMPLOYEES = gql`
  ${GET_EMPLOYEE_ONLY}
  query GetLinkedEmployees(
    $agencyId: String
    $agentId: String
    $status: EmployeeStatus
  ) {
    linkedEmployees(agencyId: $agencyId, agentId: $agentId, status: $status) {
      totalCount
      employees {
        ...getEmployeeOnly
      }
    }
  }
`;
export const GET_ALL_SHORT_LINKS = gql`
  query GetShortLinks {
    shortLinks {
      _id
      link

      employees {
        _id
        givenName
      }
    }
  }
`;
export const GET_ALL_NOTIFICATIONS = gql`
  query GetNotifications(
    $status: NotificationStatus
    $skip: Int!
    $take: Int!
  ) {
    findNotifications(skip: $skip, take: $take, status: $status) {
      totalCount
      notifications {
        _id
        message
        priority
        status
        fromUserEmail
        from {
          _id
          displayName
          header
          nameAmharic
        }
        employee {
          _id
          status
          passportNumber
          notificationMessage
          givenName
          surName
          facePhoto
          standPhoto
          passportScan
          sex
          dateOfBirth
          selectionDueDate
        }
        createdAt
      }
    }
  }
`;

export const GET_SELECTED_EMPLOYEE = gql`
  ${GET_EMPLOYEE}
  query GetSelectedEmployee($id: String!) {
    getEmployee(input: { _id: $id }) {
      ...getEmployee
    }
  }
`;

export const GET_ALL_VISAS = gql`
  ${GET_VISA}
  query GetVisas($searchText: String, $skip: Int!, $take: Int!) {
    visas(skip: $skip, take: $take, searchText: $searchText) {
      totalCount
      visas {
        ...getVisa
      }
    }
  }
`;

export const GET_SELECTED_VISA = gql`
  ${GET_VISA}
  query GetSelectedVisa($id: String!) {
    getVisa(input: { _id: $id }) {
      ...getVisa
    }
  }
`;

export const GET_ADDRESS_REGIONS = gql`
  query AddressRegions($search: String, $take: Int) {
    addressRegions(search: $search, take: $take)
  }
`;

export const GET_BROKER_NAMES = gql`
  query BrokerNames($search: String, $take: Int) {
    brokerNames(search: $search, take: $take)
  }
`;

export const CHECK_STATUS_CAPTCHA = gql`
  query recaptchaCheckStatus($passportNumber: String!, $captcha: String!) {
    recaptchaCheckStatus(
      input: { passportNumber: $passportNumber, captcha: $captcha }
    ) {
      givenName
      surName
      passportNumber
      facePhoto
      appliedCountry
      status
      currentStatusDate
      currentStatusRemark
    }
  }
`;
